import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ArrowLeft, CalendarRange, FileDown, FileSpreadsheet, Filter, Inbox } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useFinance } from '@/contexts/FinanceContext';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import { CounterpartyCombobox } from '@/features/counterparties/CounterpartyCombobox';
import { useReportData } from './useReports';
import { exportReportPdf } from './reportExportPdf';
import { exportReportCsv } from './reportExportCsv';
import { isReportKind, REPORT_LABELS, REPORT_DESCRIPTIONS, type ReportFilters } from './types';

const formatBrl = (n: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);

const NONE_VALUE = '__all__';

function parseDateInput(value: string, fallback: Date): Date {
  if (!value) return fallback;
  const [year, month, day] = value.split('-').map((s) => Number.parseInt(s, 10));
  if (!year || !month || !day) return fallback;
  return new Date(year, month - 1, day);
}

function toDateInputValue(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

export function ReportView() {
  const { kind: rawKind } = useParams();
  const { client } = useCurrentClient();
  const { accounts, costCenters, categories, counterparties } = useFinance();

  const today = new Date();
  const [fromDate, setFromDate] = useState<Date>(() => startOfMonth(today));
  const [toDate, setToDate] = useState<Date>(() => endOfMonth(today));
  const [accountId, setAccountId] = useState<string>(NONE_VALUE);
  const [counterpartyId, setCounterpartyId] = useState<string | undefined>();
  const [costCenterId, setCostCenterId] = useState<string>(NONE_VALUE);
  const [categoryId, setCategoryId] = useState<string>(NONE_VALUE);

  const filters: ReportFilters = useMemo(
    () => ({
      fromDate,
      toDate,
      ...(accountId !== NONE_VALUE && { accountId }),
      ...(counterpartyId && { counterpartyId }),
      ...(costCenterId !== NONE_VALUE && { costCenterId }),
      ...(categoryId !== NONE_VALUE && { categoryId }),
    }),
    [fromDate, toDate, accountId, counterpartyId, costCenterId, categoryId],
  );

  // Hook precisa rodar incondicionalmente — usa kind seguro como fallback enquanto
  // validamos o param de URL. Se a rota for inválida, descartamos `data` no return abaixo.
  const safeKind = rawKind && isReportKind(rawKind) ? rawKind : 'payable';
  const data = useReportData(safeKind, filters);

  if (!rawKind || !isReportKind(rawKind)) {
    return (
      <div className="rounded-xl border border-border bg-white p-8 text-center text-muted-foreground">
        <p className="text-sm">Tipo de relatório inválido.</p>
        <Button variant="outline" className="mt-4" asChild>
          <Link to="/app/relatorios">Voltar</Link>
        </Button>
      </div>
    );
  }

  const kind = rawKind;
  const isIncomeKind = kind === 'receivable' || kind === 'received';
  const filteredCategories = categories.filter((c) =>
    isIncomeKind ? c.type === 'income' : c.type === 'expense',
  );

  const selectedAccount =
    accountId !== NONE_VALUE ? accounts.find((a) => a.id === accountId) : undefined;
  const accountName = selectedAccount?.name;
  const isCreditCardReport = selectedAccount?.type === 'credit_card';
  const counterpartyName = counterpartyId
    ? counterparties.find((c) => c.id === counterpartyId)?.name
    : undefined;
  const costCenterName =
    costCenterId !== NONE_VALUE ? costCenters.find((c) => c.id === costCenterId)?.name : undefined;
  const categoryName =
    categoryId !== NONE_VALUE ? categories.find((c) => c.id === categoryId)?.name : undefined;

  const handleExportPdf = () => {
    exportReportPdf({
      kind,
      filters,
      data,
      clientName: client?.name ?? 'Cliente',
      ...(accountName && { accountName }),
      ...(counterpartyName && { counterpartyName }),
      ...(costCenterName && { costCenterName }),
      ...(categoryName && { categoryName }),
    });
  };

  const handleExportCsv = () => exportReportCsv({ kind, filters, data });

  const dateLabel = kind === 'paid' || kind === 'received' ? 'Data de pagamento' : 'Vencimento';

  return (
    <div className="space-y-5">
      <div>
        <Button variant="ghost" size="sm" className="-ml-2 mb-2" asChild>
          <Link to="/app/relatorios">
            <ArrowLeft size={14} className="mr-1.5" />
            Relatórios
          </Link>
        </Button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-brand text-2xl font-bold text-foreground">
              {isCreditCardReport ? `Fatura — ${selectedAccount.name}` : REPORT_LABELS[kind]}
            </h1>
            <p className="text-sm text-muted-foreground">
              {client?.name ? `${client.name} · ` : ''}
              {isCreditCardReport
                ? `Lançamentos no cartão entre ${format(fromDate, 'dd/MM/yyyy', { locale: ptBR })} e ${format(toDate, 'dd/MM/yyyy', { locale: ptBR })}`
                : REPORT_DESCRIPTIONS[kind]}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={handleExportCsv} disabled={data.count === 0}>
              <FileSpreadsheet size={14} className="mr-1.5" />
              Exportar CSV
            </Button>
            <Button
              onClick={handleExportPdf}
              disabled={data.count === 0}
              className="bg-accent font-semibold text-accent-foreground hover:bg-accent/90"
            >
              <FileDown size={14} className="mr-1.5" />
              Exportar PDF
            </Button>
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Filter size={14} />
            Filtros
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-1.5">
              <Label className="text-xs">{dateLabel} — De</Label>
              <Input
                type="date"
                value={toDateInputValue(fromDate)}
                onChange={(e) => setFromDate(parseDateInput(e.target.value, fromDate))}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Até</Label>
              <Input
                type="date"
                value={toDateInputValue(toDate)}
                onChange={(e) => setToDate(parseDateInput(e.target.value, toDate))}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Conta</Label>
              <Select value={accountId} onValueChange={setAccountId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_VALUE}>Todas</SelectItem>
                  {accounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{isIncomeKind ? 'Cliente' : 'Fornecedor'}</Label>
              <CounterpartyCombobox
                value={counterpartyId}
                onChange={setCounterpartyId}
                role={isIncomeKind ? 'income' : 'expense'}
                placeholder={isIncomeKind ? 'Todos os clientes' : 'Todos os fornecedores'}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Centro de custo</Label>
              <Select value={costCenterId} onValueChange={setCostCenterId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_VALUE}>Todos</SelectItem>
                  {costCenters
                    .filter((cc) => cc.isActive)
                    .map((cc) => (
                      <SelectItem key={cc.id} value={cc.id}>
                        {cc.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Categoria</Label>
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_VALUE}>Todas</SelectItem>
                  {filteredCategories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Lançamentos</p>
            <p className="mt-1 text-2xl font-bold text-foreground">{data.count}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Total</p>
            <p
              className={`mt-1 text-2xl font-bold ${isIncomeKind ? 'text-income' : 'text-expense'}`}
            >
              {formatBrl(data.totalAmount)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Ticket médio</p>
            <p className="mt-1 text-2xl font-bold text-foreground">
              {formatBrl(data.averageAmount)}
            </p>
          </CardContent>
        </Card>
      </div>

      {data.count === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-muted-foreground">
            <Inbox className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <p className="font-medium">Nenhum lançamento encontrado para os filtros aplicados.</p>
            <p className="mt-1 text-sm">
              Tente ampliar o período ou remover filtros para visualizar dados.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="flex items-center gap-2 border-b border-border bg-secondary/30 px-4 py-2 text-xs text-muted-foreground">
              <CalendarRange size={12} />
              {format(fromDate, 'dd/MM/yyyy', { locale: ptBR })} —{' '}
              {format(toDate, 'dd/MM/yyyy', { locale: ptBR })}
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">{dateLabel}</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Contraparte</TableHead>
                  <TableHead>Conta</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((r) => {
                  const dateForRow =
                    kind === 'paid' || kind === 'received'
                      ? (r.paymentDate ?? r.dueDate)
                      : r.dueDate;
                  return (
                    <TableRow key={r.id}>
                      <TableCell className="text-xs">
                        {format(dateForRow, 'dd/MM/yyyy', { locale: ptBR })}
                      </TableCell>
                      <TableCell className="max-w-[260px] truncate text-sm" title={r.description}>
                        {r.description}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {r.counterpartyName ?? '—'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {r.accountName}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {r.categorySplits && r.categorySplits.length > 0 ? (
                          <span
                            className="inline-flex cursor-help items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary"
                            title={r.categorySplits
                              .map((s) => {
                                const cat = categories.find((c) => c.id === s.categoryId);
                                return `${cat?.name ?? '—'}: ${formatBrl(s.amount)}`;
                              })
                              .join('\n')}
                          >
                            Múltiplas ({r.categorySplits.length})
                          </span>
                        ) : (
                          r.categoryName
                        )}
                      </TableCell>
                      <TableCell
                        className={`text-right text-sm font-medium tabular-nums ${
                          isIncomeKind ? 'text-income' : 'text-expense'
                        }`}
                      >
                        {(() => {
                          const principal = Number(r.amount);
                          const interestN = Number(r.interest ?? 0);
                          const fineN = Number(r.fine ?? 0);
                          const total = principal + interestN + fineN;
                          const tooltip =
                            interestN > 0 || fineN > 0
                              ? `Principal ${formatBrl(principal)}` +
                                (fineN > 0 ? ` + Multa ${formatBrl(fineN)}` : '') +
                                (interestN > 0 ? ` + Juros ${formatBrl(interestN)}` : '')
                              : undefined;
                          return (
                            <span
                              {...(tooltip && { title: tooltip })}
                              className={tooltip ? 'cursor-help underline decoration-dotted' : ''}
                            >
                              {formatBrl(total)}
                            </span>
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
